<template>
  <div>
    <b-card class="mb-1">
      <b-row align-v="end">
        <b-col cols="12" md="6">
          <h4 class="mb-25">Failed Trade Imports</h4>
          <small class="text-muted">
            Intesa confirmations matched to Bloomberg EMSX orders from sfc.trades@systemstailormade.com are
            booked automatically as verified trades with journals. The ones below could not be: fix the
            reason and press Retry.
            <span v-if="lastImportAt"> Last email read {{ formatDateTime(lastImportAt) }}.</span>
          </small>
        </b-col>
        <b-col cols="6" md="2">
          <b-form-group label="From" class="mb-0">
            <b-form-input v-model="dateFrom" type="date" @change="load" />
          </b-form-group>
        </b-col>
        <b-col cols="6" md="2">
          <b-form-group label="To" class="mb-0">
            <b-form-input v-model="dateTo" type="date" @change="load" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="2" class="mt-1 mt-md-0">
          <b-button block variant="outline-primary" :disabled="refreshing" @click="refresh">
            <b-spinner v-if="refreshing" small /> Check mailbox
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-overlay :show="loading" rounded="sm" no-fade>
      <b-card no-body class="mb-1">
        <b-card-header>
          <b-card-title>Failed ({{ failed.length }})</b-card-title>
          <small class="text-muted">All failed imports, whatever the date range</small>
        </b-card-header>

        <b-table
          :items="failed"
          :fields="failedFields"
          small
          responsive
          show-empty
          empty-text="Nothing has failed. Every matched import has been booked."
          head-variant="dark"
          class="mb-0"
        >
          <template #cell(TradeDate)="{ item }">{{ formatDate(item.TradeDate) }}</template>
          <template #cell(client)="{ item }">
            <div>{{ item.ClientAccountCode || "-" }}</div>
            <small class="text-muted">{{ item.ClientName }} · {{ item.ClientTrader }}</small>
          </template>
          <template #cell(order)="{ item }">
            <strong :class="item.BuySell === 'Buy' ? 'text-success' : 'text-danger'">{{ item.BuySell }}</strong>
            {{ formatQty(item.Quantity) }} {{ item.Ticker }} @ {{ formatPrice(item.AvgPrice) }}
            <div v-for="c in item.confirmations" :key="c.id">
              <small class="text-muted">
                Intesa {{ c.Security }} · {{ c.Isin }}
                <b-link v-if="c.PdfFileId" class="ml-25" @click="downloadFile(c.PdfFileId)">PDF</b-link>
              </small>
            </div>
          </template>
          <template #cell(commission)="{ item }">
            <template v-if="item.confirmations.length">
              <div>{{ ccy(item) }} {{ formatMoney(sum(item, "Commission") + sum(item, "OtherCharges")) }}</div>
              <small class="text-muted">
                {{ formatMoney(sum(item, "Commission")) }} + other charges {{ formatMoney(sum(item, "OtherCharges")) }}
                <span v-if="sum(item, 'OtherCharges') > 0">({{ formatBp(sum(item, "OtherCharges"), sum(item, "Principal")) }} bp)</span>
              </small>
            </template>
          </template>
          <template #cell(reason)="{ item }">
            <b-badge variant="light-danger" class="mb-25">{{ failLabel(item.FailCode) }}</b-badge>
            <div class="reason-text">{{ item.FailReason }}</div>
            <small class="text-muted">Tried {{ item.Attempts }}× · last {{ formatDateTime(item.LastAttemptAt) }}</small>
          </template>
          <template #cell(actions)="{ item }">
            <div v-if="canFix" class="text-nowrap">
              <b-button size="sm" variant="primary" class="mr-50" @click="openFix(item)">
                {{ fixFields(item.FailCode).length ? "Fix & Retry" : "Retry" }}
              </b-button>
              <b-button size="sm" variant="flat-danger" @click="reject(item)">Reject</b-button>
            </div>
          </template>
        </b-table>
      </b-card>

      <b-card v-if="waitingOrders.length || unmatchedConfirmations.length" no-body class="mb-1">
        <b-card-header>
          <b-card-title>Waiting for the other side</b-card-title>
          <small class="text-muted">Not failed: these book as soon as the matching email arrives</small>
        </b-card-header>
        <b-table
          :items="waiting"
          :fields="waitingFields"
          small
          responsive
          head-variant="light"
          class="mb-0"
        >
          <template #cell(TradeDate)="{ item }">{{ formatDate(item.TradeDate) }}</template>
          <template #cell(trade)="{ item }">
            <b-badge v-if="item.IsReversal" variant="danger" class="mr-25">Reversal</b-badge>
            {{ item.BuySell }} {{ formatQty(item.Quantity) }} {{ item.label }} @ {{ formatPrice(item.price) }}
          </template>
          <template #cell(actions)="{ item }">
            <b-link v-if="item.PdfFileId" class="mr-1" @click="downloadFile(item.PdfFileId)">PDF</b-link>
            <b-button
              v-if="canFix && item.source === 'EMSX'"
              size="sm"
              variant="flat-danger"
              @click="reject(item)"
            >Reject</b-button>
          </template>
        </b-table>
      </b-card>

      <b-card v-if="failedEmails.length" no-body class="mb-1">
        <b-card-header><b-card-title>Emails that could not be read</b-card-title></b-card-header>
        <b-table :items="failedEmails" :fields="failedEmailFields" small responsive head-variant="light" class="mb-0">
          <template #cell(ReceivedAt)="{ item }">{{ formatDateTime(item.ReceivedAt) }}</template>
        </b-table>
      </b-card>

      <b-card no-body>
        <b-card-header>
          <b-card-title>Booked automatically ({{ booked.length }})</b-card-title>
          <b-button size="sm" variant="flat-secondary" @click="showBooked = !showBooked">
            {{ showBooked ? "Hide" : "Show" }}
          </b-button>
        </b-card-header>
        <b-table
          v-if="showBooked"
          :items="booked"
          :fields="bookedFields"
          small
          responsive
          show-empty
          empty-text="No imports booked in this date range"
          head-variant="light"
          class="mb-0"
        >
          <template #cell(TradeDate)="{ item }">{{ formatDate(item.TradeDate) }}</template>
          <template #cell(trade)="{ item }">{{ item.BuySell }} {{ formatQty(item.Quantity) }} {{ item.Ticker }}</template>
          <template #cell(BookedAt)="{ item }">{{ formatDateTime(item.BookedAt) }}</template>
        </b-table>
      </b-card>
    </b-overlay>

    <b-modal
      v-model="fixModal"
      :title="fixOrder ? `Fix ${fixOrder.BuySell} ${formatQty(fixOrder.Quantity)} ${fixOrder.Ticker}` : ''"
      ok-title="Save & Retry"
      cancel-title="Cancel"
      :ok-disabled="retrying"
      @ok.prevent="retry"
    >
      <template v-if="fixOrder">
        <b-alert show variant="danger" class="p-50">{{ fixOrder.FailReason }}</b-alert>

        <b-form-group v-if="fixFields(fixOrder.FailCode).includes('client')" label="SFC client">
          <v-select
            v-model="fix.OverrideClientId"
            :options="clients"
            :reduce="(c) => c.id"
            :get-option-label="(c) => `${c.AccountName} (${c.AccountNumber})`"
            placeholder="Choose the client to book to"
          />
          <small class="text-muted">EMSX Instructions: "{{ fixOrder.Instructions }}" · manager {{ fixOrder.ClientName }}</small>
        </b-form-group>

        <template v-if="fixFields(fixOrder.FailCode).includes('security')">
          <b-form-group label="Use an existing SFC security">
            <v-select
              v-model="fix.OverrideSecurityId"
              :options="securities"
              :reduce="(s) => s.id"
              :get-option-label="(s) => `${s.SecurityTicker} · ${s.SecurityIsin}`"
              placeholder="Choose a security"
            />
          </b-form-group>
          <b-form-group label="…or create it with this ticker">
            <b-form-input v-model="fix.OverrideTicker" placeholder="e.g. MOG/A US Equity" :disabled="!!fix.OverrideSecurityId" />
          </b-form-group>
        </template>

        <template v-if="fixFields(fixOrder.FailCode).includes('market')">
          <b-form-group label="Exchange (MIC)">
            <v-select v-model="fix.OverrideExchange" :options="exchanges" taggable placeholder="e.g. XNYS" />
          </b-form-group>
          <b-form-group label="Country">
            <v-select v-model="fix.OverrideCountry" :options="countries" placeholder="e.g. United States" />
          </b-form-group>
        </template>

        <p v-if="!fixFields(fixOrder.FailCode).length" class="mb-0">
          Fix the cause in SFC first (for example create the missing account in Account Creation), then retry.
        </p>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardTitle, BRow, BCol, BFormGroup, BFormInput, BButton, BTable,
  BBadge, BLink, BAlert, BOverlay, BSpinner, BModal,
} from "bootstrap-vue";
import vSelect from "vue-select";
import axiosIns from "@/libs/axios";
import moment from "moment";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

const FAIL_LABELS = {
  client_not_found: "Client not found",
  unknown_market: "Unknown market",
  ticker_conflict: "Security conflict",
  counterparty_not_found: "Counterparty not found",
  account_missing: "Account missing",
  journal_failed: "Journal failed",
  amount_mismatch: "Amount mismatch",
  unexpected: "Unexpected error",
};

// Which inputs the Fix form shows for each failure.
const FIX_FIELDS = {
  client_not_found: ["client"],
  unknown_market: ["market"],
  ticker_conflict: ["security"],
};

export default {
  components: {
    BCard, BCardHeader, BCardTitle, BRow, BCol, BFormGroup, BFormInput, BButton, BTable,
    BBadge, BLink, BAlert, BOverlay, BSpinner, BModal, vSelect,
  },

  data() {
    return {
      dateFrom: moment().subtract(7, "days").format("YYYY-MM-DD"),
      dateTo: moment().format("YYYY-MM-DD"),
      loading: false,
      refreshing: false,
      retrying: false,
      failed: [],
      waitingOrders: [],
      unmatchedConfirmations: [],
      booked: [],
      failedEmails: [],
      lastImportAt: null,
      canFix: false,
      showBooked: false,
      clients: [],
      securities: [],
      exchanges: [],
      countries: [],
      fixModal: false,
      fixOrder: null,
      fix: {},
      failedFields: [
        { key: "TradeDate", label: "Date" },
        { key: "client", label: "Client" },
        { key: "order", label: "Trade" },
        { key: "commission", label: "Intesa commission" },
        { key: "reason", label: "Why it failed" },
        { key: "actions", label: "" },
      ],
      waitingFields: [
        { key: "TradeDate", label: "Date" },
        { key: "source", label: "Have" },
        { key: "trade", label: "Trade" },
        { key: "waitingFor", label: "Waiting for" },
        { key: "actions", label: "" },
      ],
      failedEmailFields: [
        { key: "ReceivedAt", label: "Received" },
        { key: "FromAddress", label: "From" },
        { key: "Subject", label: "Subject" },
        { key: "Error", label: "Problem" },
      ],
      bookedFields: [
        { key: "TradeDate", label: "Date" },
        { key: "ClientAccountCode", label: "Client" },
        { key: "trade", label: "Trade" },
        { key: "EquityId", label: "Trade #" },
        { key: "BookedAt", label: "Booked" },
      ],
    };
  },

  computed: {
    waiting() {
      const orders = this.waitingOrders.map((o) => ({
        ...o,
        source: "EMSX",
        label: o.Ticker,
        price: o.AvgPrice,
        waitingFor: "Intesa confirmation",
      }));
      const confirmations = this.unmatchedConfirmations.map((c) => ({
        ...c,
        source: "Intesa",
        label: c.Security,
        price: c.Price,
        waitingFor: c.IsReversal ? "Reversal: book by hand" : "EMSX order",
      }));
      return [...orders, ...confirmations];
    },
  },

  mounted() {
    this.load();
    axiosIns.get("getTradeImportOptions").then((res) => {
      this.clients = res.data.clients;
      this.securities = res.data.securities;
      this.exchanges = res.data.exchanges;
      this.countries = res.data.countries;
    });
  },

  methods: {
    load() {
      this.loading = true;
      return axiosIns
        .post("getTradeImports", { dateFrom: this.dateFrom, dateTo: this.dateTo })
        .then((res) => {
          this.failed = res.data.failed;
          this.waitingOrders = res.data.waitingOrders;
          this.unmatchedConfirmations = res.data.unmatchedConfirmations;
          this.booked = res.data.booked;
          this.failedEmails = res.data.failedEmails;
          this.lastImportAt = res.data.lastImportAt;
          this.canFix = res.data.canFix;
        })
        .catch(() => this.toast("Error", "Could not load trade imports", "danger"))
        .finally(() => {
          this.loading = false;
        });
    },

    refresh() {
      this.refreshing = true;
      axiosIns
        .post("refreshTradeImports")
        .then(() => this.load())
        .catch(() => this.toast("Error", "Could not read the trades mailbox", "danger"))
        .finally(() => {
          this.refreshing = false;
        });
    },

    openFix(order) {
      this.fixOrder = order;
      this.fix = {
        OverrideClientId: order.OverrideClientId,
        OverrideSecurityId: order.OverrideSecurityId,
        OverrideTicker: order.OverrideTicker,
        OverrideExchange: order.OverrideExchange,
        OverrideCountry: order.OverrideCountry,
      };
      this.fixModal = true;
    },

    retry() {
      this.retrying = true;
      axiosIns
        .post("retryTradeImport", { orderId: this.fixOrder.id, ...this.fix })
        .then((res) => {
          if (res.data.result === "booked") {
            this.toast("Booked", res.data.message, "success");
            this.fixModal = false;
          } else {
            this.toast("Still failing", res.data.message, "warning");
          }
          return this.load().then(() => {
            // keep the form open on the refreshed row so the new reason shows
            if (this.fixModal) this.fixOrder = this.failed.find((o) => o.id === this.fixOrder.id) || this.fixOrder;
          });
        })
        .catch((err) => {
          const message = err.response && err.response.data && err.response.data.message;
          this.toast("Error", message || "Retry failed", "danger");
        })
        .finally(() => {
          this.retrying = false;
        });
    },

    reject(order) {
      this.$swal({
        title: `Reject ${order.BuySell} ${this.formatQty(order.Quantity)} ${order.Ticker}?`,
        text: "It will not be booked. Book it by hand if needed.",
        input: "text",
        inputPlaceholder: "Reason",
        showCancelButton: true,
        confirmButtonText: "Reject",
        customClass: { confirmButton: "btn btn-danger mr-1", cancelButton: "btn btn-outline-secondary" },
        buttonsStyling: false,
      }).then((result) => {
        if (!result.isConfirmed) return;
        axiosIns
          .post("rejectTradeImport", { orderId: order.id, reason: result.value })
          .then(() => this.load())
          .catch(() => this.toast("Error", "Could not reject this import", "danger"));
      });
    },

    downloadFile(fileId) {
      axiosIns.post("downloadTradeImportFile", { fileId }, { responseType: "blob" }).then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        window.open(url, "_blank");
      });
    },

    fixFields(code) {
      return FIX_FIELDS[code] || [];
    },
    failLabel(code) {
      return FAIL_LABELS[code] || code;
    },
    sum(item, field) {
      return item.confirmations.reduce((total, c) => total + Number(c[field] || 0), 0);
    },
    ccy(item) {
      return item.confirmations.length ? item.confirmations[0].Ccy : "";
    },

    formatMoney(value) {
      return value == null ? "" : Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatPrice(value) {
      return value == null ? "" : Number(value).toLocaleString("en-GB", { maximumFractionDigits: 8 });
    },
    formatQty(value) {
      return value == null ? "" : Number(value).toLocaleString("en-GB", { maximumFractionDigits: 4 });
    },
    formatBp(part, whole) {
      return whole ? Number(((part / whole) * 10000).toFixed(3)) : 0;
    },
    formatDate(value) {
      return value ? moment(value).format("DD-MM-YYYY") : "";
    },
    formatDateTime(value) {
      return value ? moment(value).format("DD-MM-YYYY HH:mm") : "";
    },

    toast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, text, icon: variant === "success" ? "CheckIcon" : "AlertTriangleIcon", variant },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.reason-text {
  font-size: 0.85rem;
  max-width: 380px;
  white-space: normal;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
